'use client';
 
import React, { useRef, useImperativeHandle, useMemo, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
 
const ENTRY_ANIMATION = {
  initial: { rotateX: 0 },
  animate: { rotateX: 90 },
};
 
const EXIT_ANIMATION = {
  initial: { rotateX: 90 },
  animate: { rotateX: 0 },
};
 
const formatCharacter = (char) => (char === ' ' ? '\u00A0' : char);
 
const RollingText = React.forwardRef(({
  transition = { duration: 0.5, delay: 0.1, ease: 'easeOut' },
  inView = false,
  inViewMargin = '0px',
  inViewOnce = false,
  text,
  repeatInterval = 4000,
  ...props
}, ref) => {
  const localRef = useRef(null);
  const [animationKey, setAnimationKey] = useState(0);
  useImperativeHandle(ref, () => localRef.current);
 
  const inViewResult = useInView(localRef, {
    once: inViewOnce,
    margin: inViewMargin,
  });
  const isInView = !inView || inViewResult;
 
  const characters = useMemo(() => {
    if (!text) return [];
    return text.split('');
  }, [text]);

  // Resetuj animaciju svakih par sekundi
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationKey(prev => prev + 1);
    }, repeatInterval);

    return () => clearInterval(interval);
  }, [repeatInterval]);
 
 
  return (
    <span data-slot="rolling-text" {...props} ref={localRef}>
      {characters.map((char, idx) => (
        <span
          key={`${animationKey}-${idx}`}
          style={{ perspective: '1000px' }}
          className="relative inline-block w-auto"
          aria-hidden="true"
        >
          <motion.span
            className="absolute inline-block"
            style={{
              backfaceVisibility: 'hidden',
              transformOrigin: '50% 25%',
              transformStyle: 'preserve-3d',
            }}
            initial={ENTRY_ANIMATION.initial}
            animate={isInView ? ENTRY_ANIMATION.animate : ENTRY_ANIMATION.initial}
            transition={{
              ...transition,
              delay: idx * (transition?.delay ?? 0),
            }}
          >
            {formatCharacter(char)}
          </motion.span>
          <motion.span
            className="absolute inline-block"
            style={{
              backfaceVisibility: 'hidden',
              transformOrigin: '50% 100%',
              transformStyle: 'preserve-3d',
            }}
            initial={EXIT_ANIMATION.initial}
            animate={isInView ? EXIT_ANIMATION.animate : EXIT_ANIMATION.initial}
            transition={{
              ...transition,
              delay: idx * (transition?.delay ?? 0) + 0.3,
            }}
          >
            {formatCharacter(char)}
          </motion.span>
          <span className="invisible">{formatCharacter(char)}</span>
        </span>
      ))}
 
      <span className="sr-only">{text}</span>
    </span>
  );
});

RollingText.displayName = 'RollingText';
 
export { RollingText };